import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import AdminLayout from './AdminLayout';
import './AdminDashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('analytics');
  const [visitorTab, setVisitorTab] = useState('overview');
  const navigate = useNavigate();

  const fetchAllData = useCallback(async () => {
    setLoading(true);

    try {
      const [analyticsResponse, messagesResponse] = await Promise.all([
        fetch("/.netlify/functions/admin-analytics"),
        fetch("/.netlify/functions/admin-messages")
      ]);

      const analyticsData = await analyticsResponse.json();
      const messagesData = await messagesResponse.json();

      setAnalytics(analyticsData);
      setMessages(messagesData.messages || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }

    setLoading(false);
  }, []);

  const verifySession = useCallback(
    async (sessionToken) => {
      try {
        const response = await fetch("/.netlify/functions/auth", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            action: "verify",
            sessionToken: sessionToken
          })
        });

        const data = await response.json();

        if (data.success) {
          fetchAllData();
        } else {
          // Invalid session, redirect to login
          localStorage.removeItem("adminSessionToken");
          localStorage.removeItem("adminUser");
          navigate("/admin");
        }
      } catch (error) {
        console.error("Session verification error:", error);
        localStorage.removeItem("adminSessionToken");
        localStorage.removeItem("adminUser");
        navigate("/admin");
      }
    },
    [navigate, fetchAllData]
  );
  
  useEffect(() => {
    // Check authentication
    const sessionToken = localStorage.getItem('adminSessionToken');
    if (!sessionToken) {
      navigate('/admin');
      return;
    }

    // Verify session with server
    verifySession(sessionToken);
  }, [navigate, verifySession]);


  if (loading) {
    return (
      <AdminLayout>
      <div className="admin-dashboard">
        <div className="loading">Loading...</div>
      </div>
      </AdminLayout>
    );
  }

  const pageViewsData = {
    labels: analytics?.pageViews?.labels || [],
    datasets: [
      {
        label: 'Page Views',
        data: analytics?.pageViews?.data || [],
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };

  const actionsData = {
    labels: analytics?.actions?.labels || [],
    datasets: [
      {
        data: analytics?.actions?.data || [],
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40'
        ],
      },
    ],
  };

  const timelineData = {
    labels: analytics?.timeline?.labels || [],
    datasets: [
      {
        label: 'Interactions',
        data: analytics?.timeline?.data || [],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1,
      },
    ],
  };

  return (
    <AdminLayout>
      <div className="admin-dashboard">
        <nav className="admin-nav">
          <button 
            className={activeTab === 'analytics' ? 'active' : ''}
            onClick={() => setActiveTab('analytics')}
          >
            Analytics
          </button>
          <button 
            className={activeTab === 'visitors' ? 'active' : ''}
            onClick={() => setActiveTab('visitors')}
          >
            Visitors ({analytics?.uniqueVisitors || 0})
          </button>
          <button 
            className={activeTab === 'messages' ? 'active' : ''}
            onClick={() => setActiveTab('messages')}
          >
            Messages ({messages.length})
          </button>
        </nav>

        <main className="admin-content">
        {activeTab === 'analytics' && (
          <div className="analytics-section">
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Total Page Views</h3>
                <p>{analytics?.totalPageViews || 0}</p>
              </div>
              <div className="stat-card">
                <h3>CV Downloads</h3>
                <p>{analytics?.cvDownloads || 0}</p>
              </div>
              <div className="stat-card">
                <h3>Contact Submissions</h3>
                <p>{analytics?.contactSubmissions || 0}</p>
              </div>
              <div className="stat-card">
                <h3>Unique Visitors</h3>
                <p>{analytics?.uniqueVisitors || 0}</p>
              </div>
            </div>

            <div className="charts-grid">
              <div className="chart-container">
                <h3>Page Views Over Time</h3>
                <Bar data={pageViewsData} options={{ responsive: true }} />
              </div>
              <div className="chart-container">
                <h3>User Actions</h3>
                <Doughnut data={actionsData} options={{ responsive: true }} />
              </div>
              <div className="chart-container full-width">
                <h3>Activity Timeline</h3>
                <Line data={timelineData} options={{ responsive: true }} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'visitors' && (
          <div className="visitors-section">
            <div className="visitors-header">
              <h2>Visitor Analytics</h2>
              <div className="visitor-tabs">
                <button 
                  className={visitorTab === 'overview' ? 'active' : ''}
                  onClick={() => setVisitorTab('overview')}
                >
                  Overview
                </button>
                <button 
                  className={visitorTab === 'details' ? 'active' : ''}
                  onClick={() => setVisitorTab('details')}
                >
                  Visitor Details
                </button>
              </div>
            </div>

            {visitorTab === 'overview' && (
              <div className="visitor-overview">
                <div className="visitor-stats-grid">
                  <div className="stat-card">
                    <h3>Total Visitors</h3>
                    <p>{analytics?.uniqueVisitors || 0}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Recent Visitors (24h)</h3>
                    <p>{analytics?.recentVisitors || 0}</p>
                  </div>
                </div>

                <div className="visitor-charts-grid">
                  <div className="chart-container">
                    <h3>Visitors by Country</h3>
                    <Doughnut 
                      data={{
                        labels: Object.keys(analytics?.countryStats || {}),
                        datasets: [{
                          data: Object.values(analytics?.countryStats || {}),
                          backgroundColor: [
                            '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', 
                            '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF'
                          ]
                        }]
                      }} 
                      options={{ responsive: true }} 
                    />
                  </div>
                  <div className="chart-container">
                    <h3>Device Types</h3>
                    <Bar 
                      data={{
                        labels: Object.keys(analytics?.deviceStats || {}),
                        datasets: [{
                          label: 'Visitors',
                          data: Object.values(analytics?.deviceStats || {}),
                          backgroundColor: 'rgba(231, 140, 69, 0.6)',
                          borderColor: 'rgba(231, 140, 69, 1)',
                          borderWidth: 1
                        }]
                      }} 
                      options={{ responsive: true }} 
                    />
                  </div>
                </div>
              </div>
            )}

            {visitorTab === 'details' && (
              <div className="visitor-details">
                <div className="visitor-list">
                  {analytics?.visitors?.length === 0 ? (
                    <p>No visitor data available</p>
                  ) : (
                    analytics?.visitors?.map((visitor, index) => (
                      <div key={index} className="visitor-card">
                        <div className="visitor-header">
                          <h4>Visitor #{index + 1}</h4>
                          <span className="visitor-last-seen">
                            Last seen: {new Date(visitor.lastVisit).toLocaleString()}
                          </span>
                        </div>
                        <div className="visitor-info">
                          <div className="visitor-detail">
                            <strong>Location:</strong> {visitor.city}, {visitor.region}, {visitor.country}
                          </div>
                          <div className="visitor-detail">
                            <strong>IP:</strong> {visitor.ip}
                          </div>
                          <div className="visitor-detail">
                            <strong>Page Views:</strong> {visitor.pageViews}
                          </div>
                          <div className="visitor-detail">
                            <strong>Actions:</strong> {Array.from(visitor.actions).join(', ')}
                          </div>
                          <div className="visitor-detail">
                            <strong>First Visit:</strong> {new Date(visitor.firstVisit).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="messages-section">
            <div className="messages-header">
              <h2>Contact Messages</h2>
              <button onClick={fetchAllData} className="refresh-btn">Refresh</button>
            </div>
            <div className="messages-list">
              {messages.length === 0 ? (
                <p>No messages found</p>
              ) : (
                messages.map((message, index) => (
                  <div key={index} className="message-card">
                    <div className="message-header">
                      <h4>{message.name}</h4>
                      <span className="message-date">
                        {new Date(message.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="message-email">{message.email}</p>
                    <p className="message-content">{message.message}</p>
                    <div className="message-meta">
                      <span className={`status status-${message.status}`}>
                        {message.status}
                      </span>
                      <span className="ip">IP: {message.ip}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        </main>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
